/**
 * 纯数学工具函数
 *
 * 移植自后端 viewer/bridge 下的 math_utils.py / utils_rotation.py / zmp_analysis.py
 * 中的 numpy / scipy 计算。所有函数均无副作用，输入输出为 number / number[] / number[][]。
 */

// ===== 基础向量 =====

/** 向量 2-范数（np.linalg.norm） */
export function norm(v: number[]): number {
  let s = 0;
  for (let i = 0; i < v.length; i++) s += v[i] * v[i];
  return Math.sqrt(s);
}

/** 三维向量范数 */
export function norm3(x: number, y: number, z: number): number {
  return Math.sqrt(x * x + y * y + z * z);
}

/** 点积 */
export function dot(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  let s = 0;
  for (let i = 0; i < n; i++) s += a[i] * b[i];
  return s;
}

/** np.clip */
export function clip(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

/** Python 风格的取模（结果恒非负） */
export function pymod(x: number, m: number): number {
  return ((x % m) + m) % m;
}

/** 线性插值（np.interp 对单调递增 x 的单点查询） */
export function linearInterp(xs: number[], ys: number[], x: number): number {
  const n = xs.length;
  if (n === 0) return 0;
  if (x <= xs[0]) return ys[0];
  if (x >= xs[n - 1]) return ys[n - 1];
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] <= x) lo = mid;
    else hi = mid;
  }
  if (xs[hi] === xs[lo]) return ys[lo];
  const t = (x - xs[lo]) / (xs[hi] - xs[lo]);
  return ys[lo] * (1 - t) + ys[hi] * t;
}

/**
 * 自然边界三次样条（对应 scipy.interpolate.CubicSpline bc_type="natural"）。
 * 返回一个在给定 x 区间内求值的函数。
 */
export function naturalCubicSpline(xs: number[], ys: number[]): (x: number) => number {
  const n = xs.length;
  if (n < 2) return () => (ys.length ? ys[0] : 0);

  // 三对角系统求解二阶导数（自然边界：两端二阶导数为 0）
  const h: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) h[i] = xs[i + 1] - xs[i];

  const mu = new Array(n).fill(0);
  const z = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const g = 2 * (xs[i + 1] - xs[i - 1]) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / g;
    z[i] =
      (3 * ((ys[i + 1] - ys[i]) / h[i] - (ys[i] - ys[i - 1]) / h[i - 1]) -
        h[i - 1] * z[i - 1]) /
      g;
  }

  const c = new Array(n).fill(0);
  for (let i = n - 2; i >= 0; i--) {
    c[i] = z[i] - mu[i] * c[i + 1];
  }

  const b = new Array(n).fill(0);
  const d = new Array(n).fill(0);
  for (let i = 0; i < n - 1; i++) {
    b[i] = (ys[i + 1] - ys[i]) / h[i] - (h[i] * (c[i + 1] + 2 * c[i])) / 3;
    d[i] = (c[i + 1] - c[i]) / (3 * h[i]);
  }

  return (x: number): number => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    for (let j = 0; j < n - 1; j++) {
      if (x >= xs[j] && x <= xs[j + 1]) {
        i = j;
        break;
      }
    }
    const dx = x - xs[i];
    return ys[i] + b[i] * dx + c[i] * dx * dx + d[i] * dx * dx * dx;
  };
}

/** np.unwrap（默认 discont=π） */
export function unwrap(vals: number[]): number[] {
  const out = vals.slice();
  for (let i = 1; i < out.length; i++) {
    let delta = out[i] - out[i - 1];
    while (delta > Math.PI) {
      out[i] -= 2 * Math.PI;
      delta -= 2 * Math.PI;
    }
    while (delta < -Math.PI) {
      out[i] += 2 * Math.PI;
      delta += 2 * Math.PI;
    }
  }
  return out;
}

/**
 * np.gradient(axis=0, edge_order=1)
 * 对矩阵的每一列沿行方向求一阶导数（首尾用一阶差分，中间用中心差分）。
 */
export function gradientAxis0(data: number[][], dt: number): number[][] {
  const rows = data.length;
  if (rows === 0) return [];
  const cols = data[0].length;
  const out: number[][] = Array.from({ length: rows }, () => new Array(cols).fill(0));
  if (rows === 1) return out;

  for (let c = 0; c < cols; c++) {
    out[0][c] = (data[1][c] - data[0][c]) / dt;
    for (let r = 1; r < rows - 1; r++) {
      out[r][c] = (data[r + 1][c] - data[r - 1][c]) / (2 * dt);
    }
    out[rows - 1][c] = (data[rows - 1][c] - data[rows - 2][c]) / dt;
  }
  return out;
}

/** np.quantile（线性插值法，与 numpy 默认 "linear" 一致） */
export function quantile(values: number[], q: number): number {
  if (values.length === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const n = sorted.length;
  const pos = q * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  const frac = pos - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/** 0.25/0.5/0.25 权重平滑（zmp_analysis._smooth_vectors_for_zmp） */
export function smoothVectorsForZmp(vectors: number[][]): number[][] {
  const n = vectors.length;
  if (n === 0) return [];
  const dim = vectors[0].length;
  const out = vectors.map((v) => v.slice());
  if (n < 3) return out;

  for (let d = 0; d < dim; d++) {
    const raw = vectors.map((v) => v[d]);
    for (let i = 1; i < n - 1; i++) {
      out[i][d] = 0.25 * raw[i - 1] + 0.5 * raw[i] + 0.25 * raw[i + 1];
    }
  }
  return out;
}

// ===== 四元数 =====

/**
 * 四元数球面线性插值（math_utils.slerp）。
 * 输入输出均为 wxyz。与后端实现完全一致（正交分量形式）。
 */
export function slerp(q0: number[], q1: number[], t: number): number[] {
  const n0 = norm(q0);
  const n1 = norm(q1);
  let a = n0 > 0 ? q0.map((x) => x / n0) : [1, 0, 0, 0];
  let b = n1 > 0 ? q1.map((x) => x / n1) : [1, 0, 0, 0];

  let d = dot(a, b);
  if (d < 0) {
    b = b.map((x) => -x);
    d = -d;
  }
  d = clip(d, -1, 1);

  if (d > 0.9995) {
    const result = a.map((x, i) => x + t * (b[i] - x));
    const rn = norm(result);
    return rn > 0 ? result.map((x) => x / rn) : a.slice();
  }

  const theta0 = Math.acos(d);
  const theta = theta0 * t;
  const q2 = b.map((x, i) => x - a[i] * d);
  const q2n = norm(q2);
  const q2u = q2n > 0 ? q2.map((x) => x / q2n) : q2;
  return a.map((x, i) => x * Math.cos(theta) + q2u[i] * Math.sin(theta));
}

/** 四元数对数映射（math_utils.quat_log）：theta = 2*acos(w)，返回旋转向量 */
export function quatLog(q: number[]): number[] {
  const n = norm(q);
  if (n < 1e-12) return [0, 0, 0];
  const qn = q.map((x) => x / n);
  const w = clip(qn[0], -1, 1);
  const v = [qn[1], qn[2], qn[3]];
  const vnorm = norm3(v[0], v[1], v[2]);
  const theta = 2 * Math.acos(w);
  if (vnorm < 1e-12 || theta < 1e-12) return [0, 0, 0];
  const axis = v.map((x) => x / vnorm);
  return axis.map((x) => x * theta);
}

/** 四元数指数映射（math_utils.quat_exp）：旋转向量 → 四元数（wxyz） */
export function quatExp(v: number[]): number[] {
  const theta = norm3(v[0], v[1], v[2]);
  if (theta < 1e-12) return [1, 0, 0, 0];
  const axis = v.map((x) => x / theta);
  const w = Math.cos(theta / 2);
  const s = Math.sin(theta / 2);
  return [w, axis[0] * s, axis[1] * s, axis[2] * s];
}

/** 保证相邻四元数走最短路径（对符号取反） */
export function ensureShortestPathQuatSequence(quats: number[][]): number[][] {
  const out = quats.map((q) => q.slice());
  for (let i = 1; i < out.length; i++) {
    if (dot(out[i], out[i - 1]) < 0) out[i] = out[i].map((x) => -x);
  }
  return out;
}

/**
 * 四元数批量样条插值（math_utils.quat_batch_spline）。
 * keyTimes: 关键帧时刻；keyQuats: 关键帧四元数（wxyz）；queryTimes: 查询时刻。
 * K>=3 使用切空间三次样条；K<3 使用切空间线性插值（np.interp）。
 */
export function quatBatchSpline(
  keyTimes: number[],
  keyQuats: number[][],
  queryTimes: number[],
): number[][] {
  const K = keyQuats.length;
  const nQuery = queryTimes.length;
  const out: number[][] = Array.from({ length: nQuery }, () => [1, 0, 0, 0]);

  if (K === 0) return out;

  const shortest = ensureShortestPathQuatSequence(keyQuats);
  const rotvecs = shortest.map((q) => quatLog(q));

  for (let i = 0; i < nQuery; i++) {
    const t = queryTimes[i];
    const rotvec = [0, 0, 0];
    for (let d = 0; d < 3; d++) {
      const ys = rotvecs.map((rv) => rv[d]);
      if (K >= 3) {
        rotvec[d] = naturalCubicSpline(keyTimes, ys)(t);
      } else {
        rotvec[d] = linearInterp(keyTimes, ys, t);
      }
    }
    let q = quatExp(rotvec);
    const qn = norm(q);
    q = qn > 0 ? q.map((x) => x / qn) : [1, 0, 0, 0];
    out[i] = q;
  }
  return out;
}

// ===== 旋转 =====

/**
 * 欧拉角（roll, pitch, yaw）→ 四元数（wxyz）。
 * extrinsic XYZ 顺序（对应后端 utils_rotation.euler_to_quaternion）。
 */
export function eulerToQuaternion(rpy: number[]): number[] {
  const roll = rpy[0];
  const pitch = rpy[1];
  const yaw = rpy[2];

  const cr = Math.cos(roll * 0.5);
  const sr = Math.sin(roll * 0.5);
  const cp = Math.cos(pitch * 0.5);
  const sp = Math.sin(pitch * 0.5);
  const cy = Math.cos(yaw * 0.5);
  const sy = Math.sin(yaw * 0.5);

  const w = cr * cp * cy + sr * sp * sy;
  const x = sr * cp * cy - cr * sp * sy;
  const y = cr * sp * cy + sr * cp * sy;
  const z = cr * cp * sy - sr * sp * cy;
  return [w, x, y, z];
}

/**
 * 四元数（wxyz）→ 欧拉角（roll, pitch, yaw）。
 * 对应后端 utils_rotation.quaternion_to_euler。
 */
export function quaternionToEuler(q: number[]): number[] {
  const w = q[0];
  const x = q[1];
  const y = q[2];
  const z = q[3];

  const sinrCosp = 2 * (w * x + y * z);
  const cosrCosp = 1 - 2 * (x * x + y * y);
  const roll = Math.atan2(sinrCosp, cosrCosp);

  const sinp = 2 * (w * y - z * x);
  let pitch: number;
  if (Math.abs(sinp) >= 1) pitch = Math.sign(sinp) * (Math.PI / 2);
  else pitch = Math.asin(sinp);

  const sinyCosp = 2 * (w * z + x * y);
  const cosyCosp = 1 - 2 * (y * y + z * z);
  const yaw = Math.atan2(sinyCosp, cosyCosp);

  return [roll, pitch, yaw];
}

/**
 * 旋转矩阵 → 旋转向量（world_lock_ik._rotation_matrix_to_rotvec）。
 * mat 为 3x3 行主序一维数组。与后端实现完全一致。
 */
export function rotationMatrixToRotvec(mat: number[]): number[] {
  const trace = mat[0] + mat[4] + mat[8];
  const cosAngle = clip((trace - 1) * 0.5, -1, 1);
  const angle = Math.acos(cosAngle);
  // skew = [m[2,1]-m[1,2], m[0,2]-m[2,0], m[1,0]-m[0,1]]
  const skew = [mat[7] - mat[5], mat[2] - mat[6], mat[3] - mat[1]];

  if (angle < 1e-8) return skew.map((v) => 0.5 * v);

  const denom = 2 * Math.sin(angle);
  if (Math.abs(denom) < 1e-8) return [0, 0, 0];

  return skew.map((v) => (v / denom) * angle);
}

/**
 * 四元数（wxyz）→ 旋转矩阵（3x3 行主序一维数组）。
 */
export function quaternionToMatrix(q: number[]): number[] {
  const w = q[0];
  const x = q[1];
  const y = q[2];
  const z = q[3];
  return [
    1 - 2 * (y * y + z * z), 2 * (x * y - w * z), 2 * (x * z + w * y),
    2 * (x * y + w * z), 1 - 2 * (x * x + z * z), 2 * (y * z - w * x),
    2 * (x * z - w * y), 2 * (y * z + w * x), 1 - 2 * (x * x + y * y),
  ];
}

/**
 * 求解一般线性方程组 A x = b（高斯消元 + 部分主元）。
 * 返回 null 表示矩阵奇异。
 */
export function solveLinearSystem(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  if (A.length !== n) return null;
  const M = A.map((row, i) => row.slice(0, n).concat([b[i]]));

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    for (let r = col + 1; r < n; r++) {
      const factor = M[r][col] / M[col][col];
      for (let c = col; c <= n; c++) M[r][c] -= factor * M[col][c];
    }
  }

  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
}
